import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { ThreadOverview } from "../../../api/threads/threadDecoders";
import { appPaths } from "../../../shared/routing/appPaths";
import { WorkspaceLink } from "../../../shell/workspace";
import { labelOf, useVersionLabels } from "../../versions/labels/useVersionLabels";
import { versionLabelText } from "../../versions/labels/versionText";
import ThreadStatusBadge from "../status/ThreadStatusBadge";

interface RunGraphHeaderProps {
  readonly overview: ThreadOverview;
  /** The name of the Workflow the Run follows; `null` while it loads, or for a free Run. */
  readonly workflowName: string | null;
}

/**
 * The run graph's header, which the Workflow graph never has (F131): the Run's title, which
 * opens its Run view beside, its status as module C says it, and the Workflow it follows by
 * name and version (U-2 a; a version without a label, such as a draft's, by name alone),
 * which opens the Workflow graph.
 */
export default function RunGraphHeader({ overview, workflowName }: RunGraphHeaderProps) {
  const { t } = useTranslation();
  const labels = useVersionLabels(overview.projectId, { itemVersions: overview.workflow === null ? [] : [overview.workflow], collectionVersions: [] });
  const label = labelOf(labels, overview.workflow);
  const workflow = workflowName === null || label === null ? workflowName : versionLabelText(t, workflowName, label.version);
  return (
    <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <h2 className="app-title text-xl font-bold">
        <WorkspaceLink route={appPaths.thread(overview.threadId)} placement="beside" className="app-link" title={t("threads.runGraph.openRun")}>
          {overview.title}
        </WorkspaceLink>
      </h2>
      <ThreadStatusBadge status={overview.projection.display_status} />
      {overview.workflow === null || workflow === null ? null : (
        <Link to={appPaths.workflow(overview.workflow)} className="app-link text-sm underline">
          {t("threads.runGraph.workflow", { name: workflow })}
        </Link>
      )}
    </header>
  );
}
